import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, Loader2, Factory, ArrowLeft, ListChecks, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ProductionNeedsWidget } from "../components/ProductionNeedsWidget";

const STATUS_CONFIG = {
  PLANIFIE:  { label: "Planifié",  bg: "#FBF4E9", border: "#E8D3A8", text: "#8C6B2F", bar: "#C1A063" },
  EN_COURS:  { label: "En cours",  bg: "#E8EEF6", border: "#A8BBD6", text: "#1F3A66", bar: "#3A6BC1" },
  TERMINE:   { label: "Terminé",   bg: "#E8F2EC", border: "#A8C9B6", text: "#1F4E3D", bar: "#1F4E3D" },
  ANNULE:    { label: "Annulé",    bg: "#FBEEEA", border: "#E8B0A0", text: "#A04E2C", bar: "#C05C47" },
};

const VIEW_DAYS = 14;

/**
 * Planning Gantt des Ordres de Fabrication (OF).
 *
 * Vue temporelle sur 14 jours (configurable via flèches).
 * Chaque OF est représenté par une barre horizontale colorée selon son statut.
 * X-axis : jours. Y-axis : OFs triés par date planifiée puis référence.
 *
 * Source de données : GET /api/orders. Aucun nouvel endpoint backend nécessaire.
 *
 * data-testid pour tests automatisés :
 *   - production-planning-page
 *   - planning-prev-period
 *   - planning-next-period
 *   - planning-today
 *   - planning-of-row-{order_id}
 *   - planning-status-filter-{status}
 */
export default function ProductionPlanning() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilters, setStatusFilters] = useState(new Set(["PLANIFIE", "EN_COURS"]));
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1); // commence 1j avant aujourd'hui
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/orders");
      setOrders(r.data || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Génère les 14 jours visibles
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < VIEW_DAYS; i++) {
      const d = new Date(periodStart);
      d.setDate(periodStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [periodStart]);
  const periodEnd = useMemo(() => {
    const d = new Date(periodStart);
    d.setDate(periodStart.getDate() + VIEW_DAYS);
    return d;
  }, [periodStart]);

  // Filtre les OFs visibles (dans la période + statut coché)
  const visibleOrders = useMemo(() => {
    return orders
      .filter((o) => statusFilters.has(o.status))
      .filter((o) => {
        const pd = o.planned_date ? new Date(o.planned_date) : null;
        if (!pd) return false;
        return pd >= periodStart && pd < periodEnd;
      })
      .sort((a, b) => {
        const da = new Date(a.planned_date).getTime();
        const db = new Date(b.planned_date).getTime();
        if (da !== db) return da - db;
        return (a.reference || "").localeCompare(b.reference || "");
      });
  }, [orders, statusFilters, periodStart, periodEnd]);

  const counts = useMemo(() => {
    const c = { PLANIFIE: 0, EN_COURS: 0, TERMINE: 0, ANNULE: 0 };
    for (const o of orders) {
      if (c[o.status] !== undefined) c[o.status]++;
    }
    return c;
  }, [orders]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIdx = days.findIndex((d) => d.getTime() === today.getTime());

  const goPrev = () => {
    const d = new Date(periodStart);
    d.setDate(d.getDate() - VIEW_DAYS);
    setPeriodStart(d);
  };
  const goNext = () => {
    const d = new Date(periodStart);
    d.setDate(d.getDate() + VIEW_DAYS);
    setPeriodStart(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    setPeriodStart(d);
  };

  const toggleStatus = (status) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const periodLabel = `${periodStart.toLocaleDateString("fr-CH", { day: "2-digit", month: "short" })} — ${new Date(periodEnd.getTime() - 86400000).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" })}`;

  return (
    <div className="space-y-4" data-testid="production-planning-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Factory className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A1C1A]">
            Planning de fabrication
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/ordres")}
          data-testid="planning-back-to-list"
        >
          <ListChecks className="w-4 h-4 mr-1.5" /> Vue liste
        </Button>
      </div>

      {/* Phase 101 — Widget « Besoin production agrégé Nayax » (source de vérité par machine) */}
      <ProductionNeedsWidget />

      {/* Bar de période */}
      <div className="bg-white border border-[#E5E5E0] rounded-md p-3 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={goPrev}
          data-testid="planning-prev-period"
          title="14 jours précédents"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goToday}
          data-testid="planning-today"
        >
          Aujourd'hui
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goNext}
          data-testid="planning-next-period"
          title="14 jours suivants"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-1.5 text-sm text-[#575A57] ml-1">
          <Calendar className="w-3.5 h-3.5" />
          <span className="font-mono">{periodLabel}</span>
        </div>
        {/* Filtres statut */}
        <div className="ml-auto flex items-center gap-1 flex-wrap text-xs">
          {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
            const active = statusFilters.has(status);
            return (
              <button
                key={status}
                onClick={() => toggleStatus(status)}
                data-testid={`planning-status-filter-${status.toLowerCase()}`}
                className={`px-2 py-1 rounded transition-opacity border flex items-center gap-1.5 ${
                  active ? "opacity-100" : "opacity-30"
                }`}
                style={{
                  backgroundColor: cfg.bg,
                  borderColor: cfg.border,
                  color: cfg.text,
                }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.bar }} />
                {cfg.label}
                <span className="font-mono opacity-70">{counts[status]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#575A57]" data-testid="planning-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement…
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-md overflow-hidden">
          {/* En-tête jours */}
          <div className="grid border-b border-[#E5E5E0] sticky top-0 bg-[#F4F4F0] z-10" style={{ gridTemplateColumns: `220px repeat(${VIEW_DAYS}, minmax(60px, 1fr))` }}>
            <div className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-[#575A57] border-r border-[#E5E5E0]">
              OF · Référence
            </div>
            {days.map((d, i) => {
              const isToday = d.getTime() === today.getTime();
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <div
                  key={i}
                  className={`px-1 py-2 text-center border-r border-[#E5E5E0] last:border-r-0 ${
                    isToday ? "bg-[#FBF4E9]" : isWeekend ? "bg-[#FAFAF7]" : ""
                  }`}
                  data-testid={`planning-day-header-${i}`}
                >
                  <div className={`text-[10px] uppercase tracking-wider ${isToday ? "text-[#8C6B2F] font-semibold" : "text-[#575A57]"}`}>
                    {d.toLocaleDateString("fr-CH", { weekday: "short" })}
                  </div>
                  <div className={`text-sm font-mono ${isToday ? "text-[#8C6B2F] font-semibold" : "text-[#1A1C1A]"}`}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Lignes OF */}
          {visibleOrders.length === 0 ? (
            <div className="py-16 text-center text-[#575A57]" data-testid="planning-empty">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun OF planifié sur cette période avec les statuts cochés.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E0]" data-testid="planning-rows">
              {visibleOrders.map((order) => (
                <GanttRow
                  key={order.id}
                  order={order}
                  days={days}
                  todayIdx={todayIdx}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Légende */}
      <div className="text-xs text-[#575A57] flex items-center gap-3 flex-wrap">
        <span className="font-semibold">Légende :</span>
        {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: cfg.bar }} />
            <span>{cfg.label}</span>
          </div>
        ))}
        <span className="text-[#A3A6A3] ml-2">·</span>
        <span>Vue {VIEW_DAYS} jours · Cliquez sur une barre pour ouvrir l'OF</span>
      </div>
    </div>
  );
}

function GanttRow({ order, days, todayIdx }) {
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.PLANIFIE;
  const pd = new Date(order.planned_date);
  pd.setHours(0, 0, 0, 0);
  const idx = days.findIndex((d) => d.getTime() === pd.getTime());

  // Tooltip data
  const totalQty = (order.lines || []).reduce((acc, l) => acc + (l.quantity || 0), 0);
  const productCount = (order.lines || []).length;

  return (
    <div
      className="grid hover:bg-[#FAFAF7]"
      style={{ gridTemplateColumns: `220px repeat(${days.length}, minmax(60px, 1fr))` }}
      data-testid={`planning-of-row-${order.id}`}
    >
      <button
        type="button"
        onClick={() => navigate(`/ordres?focus=${order.id}`)}
        className="px-3 py-2 text-left border-r border-[#E5E5E0] hover:bg-[#F4F4F0] transition-colors min-w-0"
        title={`${productCount} produit(s), ${totalQty} barquettes total`}
      >
        <div className="text-sm font-medium text-[#1A1C1A] truncate">{order.reference}</div>
        <div className="text-[10px] text-[#575A57] mt-0.5 truncate">
          {productCount} produit{productCount > 1 ? "s" : ""} · {totalQty} barq.
        </div>
      </button>
      <div className="col-span-full grid relative" style={{ gridColumn: "2 / -1", gridTemplateColumns: `repeat(${days.length}, minmax(60px, 1fr))` }}>
        {days.map((d, i) => {
          const isToday = i === todayIdx;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div
              key={i}
              className={`border-r border-[#E5E5E0] last:border-r-0 ${isToday ? "bg-[#FBF4E9]/30" : isWeekend ? "bg-[#FAFAF7]/50" : ""}`}
            />
          );
        })}
        {idx >= 0 && (
          <button
            type="button"
            onClick={() => navigate(`/ordres?focus=${order.id}`)}
            className="absolute top-1/2 -translate-y-1/2 h-7 rounded-md border-2 px-2 text-left hover:shadow-md transition-shadow truncate"
            style={{
              left: `calc(${(idx / days.length) * 100}% + 2px)`,
              width: `calc(${(1 / days.length) * 100}% - 4px)`,
              backgroundColor: cfg.bar,
              borderColor: cfg.bar,
              color: "#FFFFFF",
            }}
            title={`${order.reference} — ${cfg.label} — ${totalQty} barquettes`}
          >
            <span className="text-[11px] font-semibold">{cfg.label}</span>
          </button>
        )}
      </div>
    </div>
  );
}
